import { defaultRangeExtractor, type Range, useVirtualizer } from '@tanstack/react-virtual';
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { cn } from '@air/utils-tailwind';

import { AgentSelectedItem } from '~/components/v2/components/private-selection-bar/agent-selection-bar/components/AgentSelectedItem';
import { AgentSelectedItemGroupHeader } from '~/components/v2/components/private-selection-bar/agent-selection-bar/components/AgentSelectedItemGroupHeader';
import { useAgentSelectionListKeyboardNav } from '~/components/v2/components/private-selection-bar/agent-selection-bar/hooks/useAgentSelectionListKeyboardNav';
import { useSelectedItemsList } from '~/components/v2/components/shared/selectionBar/hooks/useSelectedItemsList';

/** Count header lives outside the scroll area, so group headers stick to the top of the list. */
const STICKY_GROUP_HEADER_TOP = 0;
const HEADER_ESTIMATE_HEIGHT = 24;
const HEADER_TOP_PADDING = 12;
const STICKY_GROUP_HEADER_BOTTOM_PADDING = 12;
/** Room for the sticky group header so start-aligned rows aren't covered when arrowing up. */
const STICKY_GROUP_HEADER_SCROLL_PADDING =
  HEADER_ESTIMATE_HEIGHT + HEADER_TOP_PADDING + STICKY_GROUP_HEADER_BOTTOM_PADDING;
const ITEM_ROW_ESTIMATE_HEIGHT = 64;
const VIRTUAL_ROW_GAP = 4;

export type AgentSelectionListProps = {
  allowUnselect?: boolean;
  isListOpen?: boolean;
  scrollElement: HTMLElement | null;
};

export type AgentSelectionListRef = {
  handleListKeyDown: (event: React.KeyboardEvent | KeyboardEvent) => void;
};

export const AgentSelectionList = memo(
  forwardRef<AgentSelectionListRef, AgentSelectionListProps>(
    ({ allowUnselect = true, isListOpen = false, scrollElement }, ref) => {
      const activeStickyIndexRef = useRef<number | null>(null);
      const [scrollTop, setScrollTop] = useState(0);

      const { itemList } = useSelectedItemsList();

      useEffect(() => {
        if (!scrollElement) {
          setScrollTop(0);
          return;
        }

        const handleScroll = () => {
          setScrollTop(scrollElement.scrollTop);
        };

        handleScroll();
        scrollElement.addEventListener('scroll', handleScroll, { passive: true });

        return () => scrollElement.removeEventListener('scroll', handleScroll);
      }, [scrollElement]);

      const estimateSize = useCallback(
        (index: number) => {
          const item = itemList[index];
          const baseSize =
            item.type === 'header' ? HEADER_ESTIMATE_HEIGHT + HEADER_TOP_PADDING : ITEM_ROW_ESTIMATE_HEIGHT;

          return baseSize + VIRTUAL_ROW_GAP;
        },
        [itemList],
      );

      const stickyIndices = useMemo(
        () =>
          itemList
            .map((item, index) => (item.type === 'header' ? index : null))
            .filter((idx): idx is number => idx !== null),
        [itemList],
      );

      const isSticky = useCallback((index: number) => stickyIndices.includes(index), [stickyIndices]);

      const isActiveSticky = useCallback((index: number) => activeStickyIndexRef.current === index, []);

      const virtualizer = useVirtualizer({
        count: itemList.length,
        getScrollElement: () => scrollElement ?? null,
        estimateSize,
        overscan: 5,
        scrollPaddingStart: STICKY_GROUP_HEADER_SCROLL_PADDING,
        // Count header is outside this scroll container — unlike SelectionList's DropdownMenu ref,
        // scrollElement.offsetTop is sibling offset in the panel, not in-scroll padding.
        scrollMargin: 0,
        rangeExtractor: useCallback(
          (range: Range) => {
            activeStickyIndexRef.current =
              [...stickyIndices].reverse().find((index) => range.startIndex >= index) ?? null;

            const next = new Set([
              ...(activeStickyIndexRef.current !== null ? [activeStickyIndexRef.current] : []),
              ...defaultRangeExtractor(range),
            ]);

            return [...next].sort((a, b) => a - b);
          },
          [stickyIndices],
        ),
      });

      const { handleItemMouseEnter, handleListKeyDown, selectedListIndex, shouldFocusUnselectButton } =
        useAgentSelectionListKeyboardNav({
          allowUnselect,
          isListOpen,
          itemList,
          scrollElement,
          virtualizer,
        });

      useImperativeHandle(
        ref,
        () => ({
          handleListKeyDown,
        }),
        [handleListKeyDown],
      );

      const virtualItems = virtualizer.getVirtualItems();

      return (
        <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
          {virtualItems.map((virtualRow) => {
            const listItem = itemList[virtualRow.index];
            const isItemSticky = isSticky(virtualRow.index);
            const isItemActiveSticky = isActiveSticky(virtualRow.index);
            const isHeader = listItem.type === 'header';
            const isLastListItem = virtualRow.index === itemList.length - 1;
            const showStickyHeaderFrost = isHeader && isItemActiveSticky && scrollTop > 0;

            return (
              <div
                key={virtualRow.key}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                className={cn(
                  isHeader && 'px-3',
                  isItemSticky ? 'z-20' : 'z-10',
                  showStickyHeaderFrost && 'ads:bg-glass-menu/90 bg-surface-1/90 backdrop-blur-[24px]',
                  isHeader && 'pt-3',
                  isHeader && isItemActiveSticky && 'pb-3',
                  isLastListItem && !(isHeader && isItemActiveSticky) && 'pb-2',
                  !isLastListItem && !(isHeader && isItemActiveSticky) && 'pb-1',
                )}
                role={isHeader ? 'presentation' : undefined}
                style={{
                  position: isItemActiveSticky ? 'sticky' : 'absolute',
                  top: isItemActiveSticky ? STICKY_GROUP_HEADER_TOP : 0,
                  left: 0,
                  width: '100%',
                  ...(!isItemActiveSticky && {
                    transform: `translateY(${virtualRow.start}px)`,
                  }),
                }}
              >
                {listItem.type === 'header' ? (
                  <AgentSelectedItemGroupHeader listItem={listItem} />
                ) : (
                  <AgentSelectedItem
                    allowUnselect={allowUnselect}
                    isSelected={selectedListIndex === virtualRow.index}
                    item={listItem.item}
                    listItemIndex={virtualRow.index}
                    onPointerEnter={handleItemMouseEnter}
                    shouldFocusUnselect={shouldFocusUnselectButton}
                  />
                )}
              </div>
            );
          })}
        </div>
      );
    },
  ),
);

AgentSelectionList.displayName = 'AgentSelectionList';
